import { getCookie, setCookie } from 'cookie'
import axios from 'axios'
import { getApiHost } from './apiHost'

const ATTRIBUTION_COOKIE = '_ph_attribution'
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const ATTRIBUTION_KEYS = [
  ...UTM_KEYS,
  'gclid',
  'fbclid',
  'referrer',
  'landing_page',
  'ga_client_id',
  'first_touch_at',
] as const

export type WebsiteAttribution = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
  fbclid?: string
  referrer?: string
  landing_page?: string
  ga_client_id?: string
  first_touch_at?: string
}

function parseSbjsPairs(raw: string): Record<string, string> {
  return String(raw).split('|||').reduce<Record<string, string>>((acc, part) => {
    const [key, ...rest] = part.split('=')
    if (key && rest.length) acc[key] = rest.join('=')
    return acc
  }, {})
}

function compact(input: Partial<WebsiteAttribution> | Record<string, unknown>): WebsiteAttribution {
  const next: WebsiteAttribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = input[key]
    if (typeof value === 'string' && value.trim()) next[key] = value.trim()
  }
  return next
}

function sbjsUtmAttribution(raw: string): WebsiteAttribution {
  const parts = parseSbjsPairs(raw)
  return compact({
    utm_source: parts.src,
    utm_medium: parts.mdm,
    utm_campaign: parts.cmp,
    utm_term: parts.trm,
    utm_content: parts.cnt,
  })
}

function isoFromSbjsFd(fd?: string): string | undefined {
  if (!fd) return undefined
  const normalized = fd.trim().replace(' ', 'T')
  const withZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized) ? normalized : `${normalized}Z`
  const parsed = Date.parse(withZone)
  if (Number.isNaN(parsed)) return undefined
  return new Date(parsed).toISOString()
}

function readSbjsFirst(): WebsiteAttribution {
  const first = getCookie('sbjs_first')
  const current = getCookie('sbjs_current')
  const firstAdd = getCookie('sbjs_first_add')
  const currentAdd = getCookie('sbjs_current_add')
  const firstTouchAt = isoFromSbjsFd(parseSbjsPairs(firstAdd || currentAdd || '').fd)
  return compact({
    ...sbjsUtmAttribution(first || current || ''),
    first_touch_at: firstTouchAt,
    landing_page: parseSbjsPairs(firstAdd || '').ep,
    referrer: parseSbjsPairs(firstAdd || currentAdd || '').rf,
  })
}

function fillMissing(base: WebsiteAttribution, incoming: WebsiteAttribution): WebsiteAttribution {
  const next = { ...base }
  for (const key of ATTRIBUTION_KEYS) {
    if (!next[key] && incoming[key]) next[key] = incoming[key]
  }
  return next
}

function tryParseAttributionJson(raw: string): WebsiteAttribution | null {
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') return compact(parsed)
  } catch {
    // ignore malformed JSON
  }
  return null
}

function readStoredAttribution(): WebsiteAttribution {
  const raw = getCookie(ATTRIBUTION_COOKIE)
  if (!raw) return {}
  return tryParseAttributionJson(raw) ?? {}
}

function currentGaClientId(): string | undefined {
  const ga = getCookie('_ga')
  return typeof ga === 'string' && ga.trim() ? ga.trim() : undefined
}

function queryAttribution(): WebsiteAttribution {
  const params = new URLSearchParams(window.location.search)
  const next: WebsiteAttribution = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) next[key] = value
  }
  const gclid = params.get('gclid')
  const fbclid = params.get('fbclid')
  if (gclid) next.gclid = gclid
  if (fbclid) next.fbclid = fbclid
  if (document.referrer) next.referrer = document.referrer
  next.landing_page = `${window.location.pathname}${window.location.search}`
  const ga = currentGaClientId()
  if (ga) next.ga_client_id = ga
  return compact(next)
}

function withCurrentGa(attribution: WebsiteAttribution): WebsiteAttribution {
  const ga = currentGaClientId()
  if (!ga) return attribution
  return { ...attribution, ga_client_id: ga }
}

export function captureWebsiteAttribution(): WebsiteAttribution {
  const merged = withCurrentGa(fillMissing(
    fillMissing(readStoredAttribution(), readSbjsFirst()),
    queryAttribution(),
  ))
  if (!merged.first_touch_at && Object.keys(merged).length) {
    merged.first_touch_at = new Date().toISOString()
  }
  if (Object.keys(merged).length) {
    setCookie(ATTRIBUTION_COOKIE, encodeURIComponent(JSON.stringify(merged)), NINETY_DAYS_MS)
  }
  return merged
}

export function getWebsiteAttribution(): WebsiteAttribution {
  const stored = readStoredAttribution()
  if (Object.keys(stored).length) return withCurrentGa(stored)
  return captureWebsiteAttribution()
}

export function websiteAttributionHeader(): Record<string, string> {
  const attribution = getWebsiteAttribution()
  if (!Object.keys(attribution).length) return {}
  return { 'x-payhelm-attribution': JSON.stringify(attribution) }
}

function isPapayaApiRequest(config: { url?: string; baseURL?: string }): boolean {
  const requestUrl = config.url || ''
  if (!requestUrl) return false
  // Relative URLs in this SPA are papaya. Third-party clients must use absolute origins.
  if (requestUrl.startsWith('/')) return true
  try {
    const resolved = new URL(requestUrl, config.baseURL || window.location.origin)
    const api = new URL(getApiHost(window.location.hostname))
    return resolved.origin === api.origin
  } catch {
    return false
  }
}

axios.interceptors.request.use((config) => {
  if (!isPapayaApiRequest(config)) return config
  const headers = websiteAttributionHeader()
  if (headers['x-payhelm-attribution']) {
    config.headers = config.headers || {}
    Object.assign(config.headers, headers)
  }
  return config
})
